import { Field, Input } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getBuyerHostname } from "@/lib/routing/portal-urls";
import {
  isValidStoreSlug,
  sanitizeStoreSlugInput,
} from "@/lib/routing/store-slug";

/**
 * The optional store link (R2-D5) on the admin's online-store create forms —
 * the new-seller drawer and the "Add business" drawer.
 *
 * Empty is a valid answer: the server then makes the link from the store name,
 * as before. While the admin types, the field keeps only what a link can hold
 * (`sanitizeStoreSlugInput`) and previews the address the buyer will open; the
 * preview is an LTR isolate so the host reads left to right on the Arabic page.
 * An incomplete link ("ab", "my-") says why once the admin leaves the field or
 * submits, not on every keystroke.
 */
export function StoreSlugField({
  value,
  onChange,
  error,
  showInvalid,
  onBlur,
}: {
  value: string;
  onChange: (value: string) => void;
  /** A server refusal (taken / invalid), or the submit-time pattern check. */
  error?: string;
  /** True once the field was left or the form submitted. */
  showInvalid: boolean;
  onBlur?: () => void;
}) {
  const t = useTranslations();
  const invalid = value !== "" && !isValidStoreSlug(value);
  const shownError =
    error ?? (invalid && showInvalid ? t(keys.admin.storeSlugInvalid) : undefined);
  return (
    <Field
      label={t(keys.admin.storeSlugLabel)}
      htmlFor="storeSlug"
      error={shownError}
      help={
        value === "" ? (
          t(keys.admin.storeSlugHint)
        ) : (
          <>
            {t(keys.admin.storeSlugPreview)}{" "}
            <bdi dir="ltr" style={{ fontWeight: 600, color: "var(--rd-text-secondary)" }}>
              {getBuyerHostname(value)}
            </bdi>
          </>
        )
      }
    >
      <Input
        id="storeSlug"
        name="storeSlug"
        dir="ltr"
        value={value}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        maxLength={40}
        invalid={Boolean(shownError)}
        placeholder={t(keys.admin.storeSlugPlaceholder)}
        onChange={(event) => onChange(sanitizeStoreSlugInput(event.target.value))}
        onBlur={onBlur}
      />
    </Field>
  );
}
